import { type MutableRefObject } from 'react';
import type { ContainerComponent } from 'react-sweet-state';
import {
	createHook,
	createActionsHook,
	createStateHook,
	createStore,
	createContainer,
} from 'react-sweet-state';

import type { PresenceActivity, ProviderParticipant } from '@atlaskit/editor-common/collab';

import type { PresenceParticipant, PresenceSessionData } from './';

import { SitePermissionType } from '../presence/__types__/PresenceProviderUserQuery';
import { getCurrentUserFromMap } from '../presenceUtils';
import type { PresenceUser } from '../presenceTypes';

export type ParticipantsToHydrate = {
	sessions: PresenceSessionData;
} & Omit<ProviderParticipant, 'sessionId'>;

const updateParticipantSession = (
	activity: PresenceActivity | undefined,
	participant: PresenceParticipant,
	sessionId: string,
) => {
	return {
		...participant,
		sessions: {
			...participant.sessions,
			[sessionId]: {
				activity: activity ?? 'viewer',
			},
		},
	};
};

const mapAnonymousParticipant = (
	presenceId: string,
	sessions: PresenceSessionData,
): PresenceParticipant => {
	return {
		id: presenceId,
		userId: 'unidentified',
		presenceId,
		name: '',
		src: '',
		isGuest: false,
		sessions,
	};
};

// yes, all this is hella messy right now, will be refactoring and moving out of a store
const PresenceStore = createStore({
	initialState: {
		participants: new Map<string, PresenceParticipant>(),
		totalCount: 0,
	},
	actions: {
		addParticipantFromCollabSocket:
			(participant: ProviderParticipant) =>
			({ getState, setState }) => {
				const { participants, totalCount } = getState();

				// can't be null in confluence, ultimately is the userId for logged in users and presenceId for anonymous
				const { presenceId: id } = participant;
				const currentParticipants = new Map(participants);

				if (id) {
					const previousParticipant = currentParticipants.get(id);

					const entry: PresenceParticipant = {
						...participant,
						// need to determine if entry
						isCurrentUser: previousParticipant?.isCurrentUser ?? false,
						id,
						src: participant.avatar,
						sessions: {
							...previousParticipant?.sessions,
							[participant.sessionId]: {
								activity: participant.presenceActivity ?? 'editor',
							},
						},
					};

					currentParticipants.set(id, entry);
					if (!previousParticipant) {
						setState({ participants: currentParticipants, totalCount: totalCount + 1 });
					} else {
						setState({ participants: currentParticipants });
					}
				}
			},
		addParticipant:
			(
				participant: ProviderParticipant,
				participantsToHydrateRef: MutableRefObject<Record<string, ParticipantsToHydrate>>,
			) =>
			({ getState, setState }) => {
				const { participants, totalCount } = getState();

				const joinedParticipant = participant;
				const key = joinedParticipant.presenceId ?? joinedParticipant.userId;
				const hydratedParticipant = participants.get(key);
				if (hydratedParticipant) {
					const currentParticipants = new Map(participants);
					currentParticipants.set(
						key,
						updateParticipantSession(
							joinedParticipant.presenceActivity,
							hydratedParticipant,
							joinedParticipant.sessionId,
						),
					);
					setState({ participants: currentParticipants });
				} else {
					const { sessionId, presenceActivity: activity, userId } = joinedParticipant;

					const previousEntry = participantsToHydrateRef.current[key];

					const entry = { [sessionId]: { activity: activity ?? 'viewer' } };

					const hasPreviousEntry = !!previousEntry?.sessions;

					// we can run into cases where we have multiple sessions for the same user, but have yet to fully hydrate them
					// so ensure our sessions list is up-tp-date
					const sessions: PresenceSessionData = hasPreviousEntry
						? {
								...previousEntry?.sessions,
								...entry,
							}
						: entry;

					participantsToHydrateRef.current[key] = {
						...joinedParticipant,
						sessions,
					};

					if (!hasPreviousEntry) {
						setState({ totalCount: totalCount + 1 });
					}

					// We can skip hydration for anonymous users, perhaps I should bump this higher up to short circuit
					// more of this logic ... this is getting quite messy
					const isAnonymous = userId === 'unidentified';

					if (isAnonymous) {
						const currentParticipants = new Map(participants);

						currentParticipants.set(key, mapAnonymousParticipant(key, sessions));

						setState({ participants: currentParticipants });

						delete participantsToHydrateRef.current[key];
					}
				}
			},
		addHydratedParticipant:
			(
				participant: PresenceUser & { presenceId: string; sessions: PresenceSessionData },
				participantsToHydrateRef: MutableRefObject<Record<string, ParticipantsToHydrate>>,
			) =>
			({ getState, setState }) => {
				const { participants } = getState();

				const user = participant;
				// can't be null in confluence, ultimately is the userId for logged in users and presenceId for anonymous
				const { presenceId: id, sessions } = user;
				const userId = (user as any)?.accountId ?? 'unidentified';

				const currentParticipants = new Map(participants);

				if (id) {
					const previousParticipant = currentParticipants.get(id);

					const entry: PresenceParticipant = {
						id,
						userId,
						src: user.avatarPath,
						isGuest: user.permissionType === SitePermissionType.EXTERNAL ?? undefined,
						sessions,
						presenceId: id,
						name: user.name,
						isCurrentUser: user.isCurrentUser ?? false,
					};

					let dirty = false;
					if (!previousParticipant) {
						currentParticipants.set(id, entry);
						dirty = true;
					}

					// still triggers a state change even if object hasn't changed,
					// so helping out a bit to prevent a extra re-renders
					if (dirty) {
						setState({ participants: currentParticipants });
					}

					delete participantsToHydrateRef.current[id];
				}
			},
		removeParticipant:
			(
				sessionId: string,
				participantsToHydrateRef: MutableRefObject<Record<string, ParticipantsToHydrate>>,
			) =>
			({ getState, setState }) => {
				const { participants, totalCount } = getState();

				const nonHydrated = Object.keys(participantsToHydrateRef.current).find((key) => {
					return participantsToHydrateRef.current[key].sessions[sessionId];
				});

				if (nonHydrated) {
					delete participantsToHydrateRef.current[nonHydrated];

					setState({
						totalCount: totalCount - 1,
					});
				} else {
					const currentParticipants = new Map(participants);
					const participant = getCurrentUserFromMap(participants, sessionId);

					if (participant) {
						if (Object.keys(participant.sessions).length === 1) {
							currentParticipants.delete(participant.id);

							setState({
								participants: currentParticipants,
								totalCount: totalCount - 1,
							});
						} else {
							delete participant.sessions[sessionId];

							currentParticipants.set(participant.id, participant);

							setState({
								participants: currentParticipants,
							});
						}
					}
				}
			},
	},
	name: 'PresenceStore',
});

export const usePresenceStore = createHook(PresenceStore);
export const usePresenceStoreActions = createActionsHook(PresenceStore);
export const usePresenceStoreState = createStateHook(PresenceStore);

type PresenceStoreContainerProps = {
	participant?: PresenceParticipant;
};

export const PresenceStoreContainer: ContainerComponent<PresenceStoreContainerProps> =
	createContainer(PresenceStore, {
		onInit:
			() =>
			({ setState }, { participant }) => {
				const defaultParticipants = new Map<string, PresenceParticipant>();

				if (participant) {
					defaultParticipants.set(participant.id, {
						...participant,
					});
				}

				setState({
					participants: defaultParticipants,
					totalCount: defaultParticipants.size,
				});
			},
	});
